/**
 * Rate Cards — Super Admin CRUD tab inside Commissions.
 *
 * Displays every configured rate card. Super Admin can edit key fields
 * and provide a mandatory audit reason on save. Every edit is versioned
 * and pushed into the rate_card.history array server-side.
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, Pencil, RefreshCw, History, RotateCcw } from "lucide-react";
import { useAuth } from "@/lib/auth";

const inr = (n) =>
  n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN");

const CATEGORY_LABEL = {
  education: "Education",
  financial: "Financial",
  investment: "Investment",
  admission: "Admissions",
};

export default function RateCardsTab() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [history, setHistory] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/earnings/rate-cards?include_inactive=true")
      .then(r => setRows(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const grouped = rows.reduce((acc, r) => {
    (acc[r.product_category] = acc[r.product_category] || []).push(r);
    return acc;
  }, {});

  const openHistory = async (rc) => {
    setHistoryFor(rc);
    try {
      const h = await api.get(`/earnings/rate-cards/${rc.id}/history`);
      setHistory(h.data || []);
    } catch { setHistory([]); }
  };

  return (
    <div className="space-y-4" data-testid="rate-cards-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Rate Cards</h3>
          <div className="text-xs text-slate-500">
            {rows.length} products · configurable by Super Admin · every change is audited
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load}
                data-testid="rate-cards-refresh-btn">
          <RefreshCw size={14} className="mr-1"/> Refresh
        </Button>
      </div>

      {loading && <Loader2 className="animate-spin" size={16}/>}

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat}>
          <div className="text-xs uppercase text-slate-500 tracking-wider mb-2">
            {CATEGORY_LABEL[cat] || cat}
          </div>
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-600">
                <tr>
                  <th className="p-2">Product</th>
                  <th className="p-2">Type</th>
                  <th className="p-2 text-right">Fee</th>
                  <th className="p-2 text-right">Direct cost</th>
                  <th className="p-2 text-right">Std rate</th>
                  <th className="p-2 text-right">High-vol</th>
                  <th className="p-2 text-right">TDS</th>
                  <th className="p-2 text-right">Ver</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map(r => (
                  <tr key={r.id} className={`border-t ${!r.is_active ? "opacity-50" : ""}`}
                      data-testid={`rate-card-row-${r.product_id}`}>
                    <td className="p-2">
                      <div className="font-medium">{r.product_name}</div>
                      <div className="text-xs text-slate-500">{r.product_id}</div>
                    </td>
                    <td className="p-2 text-xs">{r.calculation_type}</td>
                    <td className="p-2 text-right">{r.customer_fee ? inr(r.customer_fee) : "—"}</td>
                    <td className="p-2 text-right">{r.direct_cost ? inr(r.direct_cost) : "—"}</td>
                    <td className="p-2 text-right">{formatRate(r.standard_partner_rate, r.calculation_type)}</td>
                    <td className="p-2 text-right">{formatRate(r.high_volume_partner_rate, r.calculation_type)}</td>
                    <td className="p-2 text-right">{(r.tds_rate * 100).toFixed(1)}%</td>
                    <td className="p-2 text-right">
                      <Badge variant="secondary">v{r.rate_card_version}</Badge>
                    </td>
                    <td className="p-2 text-right space-x-1">
                      <Button size="sm" variant="ghost"
                              onClick={() => openHistory(r)}
                              data-testid={`rate-card-history-${r.product_id}`}>
                        <History size={14}/>
                      </Button>
                      {canEdit && (
                        <Button size="sm" variant="outline"
                                onClick={() => setEditing({ ...r, reason: "" })}
                                data-testid={`rate-card-edit-${r.product_id}`}>
                          <Pencil size={14}/>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      ))}

      {canEdit && <OnboardingFeesCard/>}

      {canEdit && <LanguageCourseCatalog />}

      {editing && (
        <EditDialog
          card={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {historyFor && (
        <Dialog open onOpenChange={() => setHistoryFor(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>History — {historyFor.product_name}</DialogTitle>
            </DialogHeader>
            {history.length === 0 ? (
              <div className="text-sm text-slate-500 p-4 text-center">
                No changes recorded yet.
              </div>
            ) : (
              <div className="max-h-96 overflow-auto space-y-2 text-xs">
                {history.slice().reverse().map((h, i) => (
                  <Card key={i} className="p-3">
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">
                        {new Date(h.changed_at).toLocaleString()}
                      </span>
                      <span className="text-slate-500">{h.changed_by}</span>
                    </div>
                    <div className="text-slate-600 italic mb-1">
                      {h.reason || "(no reason)"}
                    </div>
                    <pre className="bg-slate-50 p-2 text-[10px] overflow-auto rounded">
                      before: {JSON.stringify(h.before, null, 2)}
                      {"\n"}after:  {JSON.stringify(h.after, null, 2)}
                    </pre>
                  </Card>
                ))}
              </div>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}


function formatRate(r, ct) {
  if (r == null) return "—";
  if (["fixed_payout", "investment_ticket_range", "fixed_range"].includes(ct)) {
    return inr(r);
  }
  // percentages
  return (r * 100).toFixed(2) + "%";
}


function EditDialog({ card, onClose, onSaved }) {
  const isPercent = !["fixed_payout", "investment_ticket_range", "fixed_range"]
    .includes(card.calculation_type);

  const [form, setForm] = useState({
    customer_fee: card.customer_fee,
    direct_cost: card.direct_cost,
    standard_partner_rate: card.standard_partner_rate,
    high_volume_partner_rate: card.high_volume_partner_rate,
    franchise_partner_rate: card.franchise_partner_rate,
    high_volume_threshold: card.high_volume_threshold,
    provider_commission_min: card.provider_commission_min,
    provider_commission_max: card.provider_commission_max,
    tds_rate: card.tds_rate,
    is_active: card.is_active,
    reason: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const save = async () => {
    if (!form.reason || form.reason.trim().length < 3) {
      setErr("Please provide a reason for the change (audit requirement)");
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const patch = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== "" && v !== null && v !== undefined) patch[k] = v;
      });
      // Convert numeric fields
      ["customer_fee", "direct_cost", "standard_partner_rate",
       "high_volume_partner_rate", "franchise_partner_rate",
       "provider_commission_min", "provider_commission_max", "tds_rate",
      ].forEach(k => { if (patch[k] !== undefined) patch[k] = Number(patch[k]); });
      if (patch.high_volume_threshold !== undefined)
        patch.high_volume_threshold = parseInt(patch.high_volume_threshold, 10);

      await api.patch(`/earnings/rate-cards/${card.id}`, patch);
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="rate-card-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit — {card.product_name}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Customer fee (₹)" k="customer_fee" form={form} setForm={setForm}/>
          <Field label="Direct cost (₹)" k="direct_cost" form={form} setForm={setForm}/>
          <Field label={`Standard rate${isPercent ? " (0..1)" : " (₹)"}`}
                 k="standard_partner_rate" step="any" form={form} setForm={setForm}/>
          <Field label={`High-volume rate${isPercent ? " (0..1)" : " (₹)"}`}
                 k="high_volume_partner_rate" step="any" form={form} setForm={setForm}/>
          <Field label={`Franchise rate${isPercent ? " (0..1)" : " (₹)"}`}
                 k="franchise_partner_rate" step="any" form={form} setForm={setForm}/>
          <Field label="High-volume threshold"
                 k="high_volume_threshold" form={form} setForm={setForm}/>
          <Field label="Provider commission min (0..1 or ₹)"
                 k="provider_commission_min" step="any" form={form} setForm={setForm}/>
          <Field label="Provider commission max (0..1 or ₹)"
                 k="provider_commission_max" step="any" form={form} setForm={setForm}/>
          <Field label="TDS rate (0.02 = 2%)"
                 k="tds_rate" step="any" form={form} setForm={setForm}/>
          <div className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={form.is_active}
                   onChange={e => setForm(s => ({...s, is_active: e.target.checked}))}
                   data-testid="rate-card-is-active"/>
            <Label className="text-sm">Active (partners can see & calculate)</Label>
          </div>
        </div>
        <div className="mt-3">
          <Label>Reason for change (audit)</Label>
          <Input value={form.reason}
                 data-testid="rate-card-reason"
                 onChange={e => setForm(s => ({ ...s, reason: e.target.value }))}
                 placeholder="e.g. Vendor increased delivery cost by ₹1000"/>
        </div>
        {err && <div className="text-red-600 text-sm mt-2">{err}</div>}
        <DialogFooter>
          <ResetDefaultsButton card={card} onReset={onSaved}/>
          <div className="flex-1"/>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving}
                  data-testid="rate-card-save-btn">
            {saving ? <Loader2 className="animate-spin" size={14}/> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ResetDefaultsButton({ card, onReset }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const doReset = async () => {
    const reason = window.prompt(
      `Reset "${card.product_name}" to shipped spec defaults?\n\n`
      + "This will overwrite fee, direct cost, all partner rates, "
      + "TDS and provider commission range with the original values. "
      + "Enter a short reason (required for audit):",
    );
    if (!reason || reason.trim().length < 3) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post(`/earnings/rate-cards/${card.id}/reset-defaults`,
                     { reason: reason.trim() });
      onReset();
    } catch (e) {
      setErr(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={doReset}
        disabled={busy}
        title="Restore all fields to shipped spec values"
        data-testid="rate-card-reset-defaults-btn"
        className="text-amber-700 hover:text-amber-900"
      >
        {busy
          ? <Loader2 className="animate-spin" size={14}/>
          : <RotateCcw size={14} className="mr-1"/>}
        Reset to defaults
      </Button>
      {err && <span className="text-xs text-red-600 self-center">{err}</span>}
    </>
  );
}


function Field({ label, k, form, setForm, step }) {
  return (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input
        data-testid={`rate-card-field-${k}`}
        type="number" step={step || "1"}
        value={form[k] ?? ""}
        onChange={e => setForm(s =>
          ({ ...s, [k]: e.target.value === "" ? "" : e.target.value }))}
      />
    </div>
  );
}



/* ---------------------------------------------------------------------------
 * Language / Test-Prep course catalog editor (super_admin only)
 * ------------------------------------------------------------------------- */
function LanguageCourseCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);   // null | "new" | course
  const [includeInactive, setIncludeInactive] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/earnings/language-courses?include_inactive=${includeInactive}`);
      setRows(data.courses || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const grouped = rows.reduce((acc, c) => {
    (acc[c.category] ||= []).push(c); return acc;
  }, {});

  const totalActive = rows.filter(r => r.is_active).length;

  return (
    <div className="pt-6 border-t border-slate-200"
         data-testid="lang-courses-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold">
            Language / Test-Preparation catalog
          </h3>
          <p className="text-xs text-slate-500">
            {totalActive} active courses. Direct cost is confidential and
            never appears in partner responses.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs flex items-center gap-1">
            <input type="checkbox" checked={includeInactive}
                   onChange={e => setIncludeInactive(e.target.checked)}
                   data-testid="lang-courses-toggle-inactive"/>
            Show inactive
          </label>
          <Button size="sm" variant="outline" onClick={load}
                  data-testid="lang-courses-refresh">
            <RefreshCw className="h-3.5 w-3.5 mr-1"/>Refresh
          </Button>
          <Button size="sm" onClick={() => setEditing("new")}
                  data-testid="lang-courses-add-btn">
            + Add course
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="p-6 text-center text-sm text-slate-500">
          <Loader2 className="h-4 w-4 inline animate-spin mr-1"/>Loading…
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat} className="mb-4">
            <div className="text-sm font-medium text-slate-700 mb-1">{cat}</div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Course</th>
                    <th className="text-right px-3 py-2">Fee</th>
                    <th className="text-right px-3 py-2">Direct cost</th>
                    <th className="text-right px-3 py-2">Net payout*</th>
                    <th className="text-right px-3 py-2 w-40">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(c => {
                    const gross = Math.round((c.fee - c.direct_cost) * 0.7);
                    const tds = Math.round(gross * 0.02);
                    const net = gross - tds;
                    return (
                      <tr key={c.id}
                          className={"border-t " + (c.is_active ? "" : "opacity-50")}
                          data-testid={`lang-course-row-${c.id}`}>
                        <td className="px-3 py-2">
                          {c.name}
                          {!c.is_active && (
                            <Badge variant="outline" className="ml-2 text-xs">inactive</Badge>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{inr(c.fee)}</td>
                        <td className="px-3 py-2 text-right">{inr(c.direct_cost)}</td>
                        <td className="px-3 py-2 text-right font-medium text-emerald-700">
                          {inr(net)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button size="sm" variant="ghost"
                                  onClick={() => setEditing(c)}
                                  data-testid={`lang-course-edit-${c.id}`}>
                            <Pencil className="h-3.5 w-3.5"/>
                          </Button>
                          {c.is_active && (
                            <Button size="sm" variant="ghost"
                                    onClick={async () => {
                                      if (!window.confirm(`Deactivate "${c.name}"?`)) return;
                                      await api.delete(`/earnings/language-courses/${c.id}`);
                                      load();
                                    }}
                                    data-testid={`lang-course-delete-${c.id}`}>
                              <RotateCcw className="h-3.5 w-3.5 text-red-500"/>
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          </div>
        ))
      )}
      <p className="text-[11px] text-slate-400 mt-1">
        * Indicative net payout uses the standard 70% partner share and 2% TDS.
      </p>

      {editing && (
        <LanguageCourseEditor
          course={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}


function LanguageCourseEditor({ course, onClose, onSaved }) {
  const isNew = !course;
  const [form, setForm] = useState({
    category:    course?.category    ?? "English & Test Preparation",
    name:        course?.name        ?? "",
    fee:         course?.fee         ?? "",
    direct_cost: course?.direct_cost ?? "",
    is_active:   course?.is_active   ?? true,
    notes:       course?.notes       ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async () => {
    setError(null);
    if (!form.name.trim() || form.fee === "" || form.direct_cost === "") {
      setError("Name, fee and direct cost are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        category: form.category,
        name: form.name.trim(),
        fee: Number(form.fee),
        direct_cost: Number(form.direct_cost),
        is_active: form.is_active,
        notes: form.notes || null,
      };
      if (isNew) {
        await api.post("/earnings/language-courses", body);
      } else {
        await api.patch(`/earnings/language-courses/${course.id}`, body);
      }
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="lang-course-editor">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Add course" : `Edit — ${course.name}`}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Category</Label>
            <select className="w-full border rounded px-2 py-1.5 text-sm"
                    value={form.category}
                    onChange={e => setForm(s => ({ ...s, category: e.target.value }))}
                    data-testid="lang-course-field-category">
              {["English & Test Preparation","French","German",
                "GRE, SAT & GMAT","Career Skills"].map(c =>
                <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <Label>Course name</Label>
            <Input value={form.name}
                   onChange={e => setForm(s => ({ ...s, name: e.target.value }))}
                   data-testid="lang-course-field-name"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Customer fee (₹)</Label>
              <Input type="number" value={form.fee}
                     onChange={e => setForm(s => ({ ...s, fee: e.target.value }))}
                     data-testid="lang-course-field-fee"/>
            </div>
            <div>
              <Label>Direct cost (₹)</Label>
              <Input type="number" value={form.direct_cost}
                     onChange={e => setForm(s => ({ ...s, direct_cost: e.target.value }))}
                     data-testid="lang-course-field-direct-cost"/>
            </div>
          </div>
          <div>
            <Label>Notes (optional)</Label>
            <Input value={form.notes || ""}
                   onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
                   data-testid="lang-course-field-notes"/>
          </div>
          {!isNew && (
            <label className="text-sm flex items-center gap-2">
              <input type="checkbox" checked={form.is_active}
                     onChange={e => setForm(s => ({ ...s, is_active: e.target.checked }))}
                     data-testid="lang-course-field-active"/>
              Active
            </label>
          )}
          {error && (
            <div className="text-sm text-red-600" data-testid="lang-course-error">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}
                  data-testid="lang-course-cancel-btn">Cancel</Button>
          <Button onClick={save} disabled={saving}
                  data-testid="lang-course-save-btn">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin"/>}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------------------------------------------------------------------
 * Onboarding fees (Partner membership + Franchise fee + GST) — super_admin only
 * ------------------------------------------------------------------------- */
function OnboardingFeesCard() {
  const [fees, setFees] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/partner-applications/onboarding-fees");
      setFees(data);
      setForm({
        partner_membership_fee: data.partner_membership_fee,
        franchise_fee: data.franchise_fee,
        gst_rate: (data.gst_rate * 100).toFixed(2),  // display as %
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load onboarding fees");
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setError(null); setSavedAt(null); setSaving(true);
    try {
      const payload = {
        partner_membership_fee: Number(form.partner_membership_fee),
        franchise_fee: Number(form.franchise_fee),
        gst_rate: Number(form.gst_rate) / 100,   // back to fraction
      };
      const { data } = await api.patch(
        "/partner-applications/onboarding-fees", payload);
      setFees(data);
      setSavedAt(new Date());
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (!fees) {
    return (
      <div className="pt-6 border-t border-slate-200">
        <div className="text-sm text-slate-500">
          <Loader2 className="h-4 w-4 inline animate-spin mr-1"/>Loading onboarding fees…
        </div>
      </div>
    );
  }

  const membershipGst =
    Math.round(Number(form.partner_membership_fee || 0) * Number(form.gst_rate || 0) / 100);
  const totalProfessional = Number(form.partner_membership_fee || 0) + membershipGst;
  const franchiseGst =
    Math.round(Number(form.franchise_fee || 0) * Number(form.gst_rate || 0) / 100);
  const totalFranchise = Number(form.franchise_fee || 0) + franchiseGst
                       + Number(form.partner_membership_fee || 0);

  return (
    <div className="pt-6 border-t border-slate-200" data-testid="onboarding-fees-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-lg font-semibold">Onboarding fees</h3>
          <p className="text-xs text-slate-500">
            One-time joining fees shown to applicants on Partner Register &amp;
            payment pages. Editable by super_admin only. Changes take effect
            immediately for new applications — already-submitted applications
            keep their original frozen fee snapshot.
          </p>
        </div>
        {savedAt && (
          <span className="text-xs text-emerald-700"
                data-testid="onboarding-fees-saved">
            Saved · {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>

      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label>Partner membership fee (₹)</Label>
            <Input type="number" min={0}
                   value={form.partner_membership_fee ?? ""}
                   onChange={e => setForm(s => ({ ...s, partner_membership_fee: e.target.value }))}
                   data-testid="onboarding-fees-membership"/>
            <p className="text-[11px] text-slate-500 mt-1">
              Applies to every partner (Professional &amp; Franchise).
            </p>
          </div>
          <div>
            <Label>Franchise fee (₹)</Label>
            <Input type="number" min={0}
                   value={form.franchise_fee ?? ""}
                   onChange={e => setForm(s => ({ ...s, franchise_fee: e.target.value }))}
                   data-testid="onboarding-fees-franchise"/>
            <p className="text-[11px] text-slate-500 mt-1">
              Only charged to Franchise applicants.
            </p>
          </div>
          <div>
            <Label>GST rate (%)</Label>
            <Input type="number" min={0} max={100} step="0.01"
                   value={form.gst_rate ?? ""}
                   onChange={e => setForm(s => ({ ...s, gst_rate: e.target.value }))}
                   data-testid="onboarding-fees-gst"/>
            <p className="text-[11px] text-slate-500 mt-1">
              Applied to the franchise fee and the professional membership. Default 18%.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Card className="p-3 bg-slate-50 border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Professional partner total</div>
            <div className="text-lg font-semibold" data-testid="onboarding-fees-total-pro">
              {inr(totalProfessional)}
            </div>
            <div className="text-xs text-slate-500">Membership + GST {inr(membershipGst)}.</div>
          </Card>
          <Card className="p-3 bg-slate-50 border-slate-200">
            <div className="text-xs text-slate-500 mb-1">Franchise partner total</div>
            <div className="text-lg font-semibold" data-testid="onboarding-fees-total-franchise">
              {inr(totalFranchise)}
            </div>
            <div className="text-xs text-slate-500">
              Franchise {inr(Number(form.franchise_fee || 0))} + GST {inr(franchiseGst)} + Membership {inr(Number(form.partner_membership_fee || 0))}.
            </div>
          </Card>
        </div>

        {error && (
          <div className="text-sm text-red-600" data-testid="onboarding-fees-error">{error}</div>
        )}

        <div className="flex justify-between text-xs text-slate-500">
          <span>
            Last updated: {fees.updated_at
              ? new Date(fees.updated_at).toLocaleString()
              : "never"}
            {fees.updated_by && (<> by <b>{fees.updated_by}</b></>)}
          </span>
          <Button onClick={save} disabled={saving}
                  data-testid="onboarding-fees-save-btn">
            {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin"/>}
            Save changes
          </Button>
        </div>
      </Card>
    </div>
  );
}

